/**
 * TotalConnectAccessGate - Access control wrapper for Total Connect features
 * 
 * All Total Connect features require an active mailbox. This component:
 * - Shows a friendly message when user doesn't have a mailbox
 * - Provides a link to request a mailbox from admin
 * - Wraps children to show only when access is granted
 * 
 * @author TotalAccess Development Team
 * @date January 2026
 */

'use client';

import React, { useState, useMemo } from 'react';
import { useWorkEmail } from '@/hooks/useWorkEmail';
import { useAccessibleEmailClients } from '@/hooks/useAccessibleEmailClients';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';
import { cn } from '@/lib/utils';
import { Mail, AlertCircle, Loader2, UserPlus, X, Send, Lock } from 'lucide-react';
import { notify } from '@/lib/notifications';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface TotalConnectAccessGateProps {
  children: React.ReactNode;
  /** Feature name for the blocked message */
  featureName?: string;
  /** Show a compact message instead of full-page */
  compact?: boolean;
  /** Show loading state while checking access */
  showLoading?: boolean;
  /** Custom message when access is blocked */
  customBlockedMessage?: string;
  /** Callback when access is blocked */
  onAccessBlocked?: () => void;
  /** When false, checks module access instead of requiring an active mailbox. Owner/root always allowed. */
  requireMailbox?: boolean;
  /** Module key to check access for when requireMailbox is false */
  moduleKey?: string;
}

export function TotalConnectAccessGate({
  children,
  featureName = 'Total Connect',
  compact = false,
  showLoading = true,
  customBlockedMessage,
  onAccessBlocked,
  requireMailbox = true,
  moduleKey,
}: TotalConnectAccessGateProps) {
  const {
    hasActiveMailbox,
    isLoading: emailLoading,
    accessBlockedReason,
  } = useWorkEmail();
  // User-centric email access — true when the user has an active/virtual
  // mailbox at ANY client, not just the JWT active client. This lets a user
  // with mailboxes across multiple clients access Total Connect features
  // (Outlook-style multi-mailbox) even when the active client has no mailbox.
  const {
    hasAnyMailbox: hasAnyMailboxCrossClient,
    loading: accessibleEmailLoading,
  } = useAccessibleEmailClients();
  const { currentTheme, clientBranding } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || currentTheme?.colors?.primary || '#3b82f6';

  // For module-based access checks (e.g. cloud storage without active email)
  const { user, isRoot } = useAuth();
  const { canAccessModule, loading: navLoading } = useNavigation();

  const isOwnerOrRoot = isRoot || user?.role === 'owner';

  const hasModuleAccess = useMemo(() => {
    if (!moduleKey) return false;
    return canAccessModule(moduleKey);
  }, [canAccessModule, moduleKey]);

  const effectiveAccessGranted = useMemo(() => {
    if (requireMailbox) {
      // Active-client mailbox grants access immediately.
      if (hasActiveMailbox) return true;
      // Otherwise, fall back to user-centric check: if the user has a mailbox
      // at any client, grant access (multi-client / Outlook-style).
      if (hasAnyMailboxCrossClient) return true;
      return false;
    }
    // Owner/root always see the feature regardless of module access
    if (isOwnerOrRoot) return true;
    // Otherwise require the specified module key
    return hasModuleAccess;
  }, [requireMailbox, hasActiveMailbox, hasAnyMailboxCrossClient, isOwnerOrRoot, hasModuleAccess]);

  // When requireMailbox, we're checking until both the active-client work email
  // check and the cross-client accessible-clients check have settled.
  const isChecking = requireMailbox
    ? (emailLoading || accessibleEmailLoading)
    : navLoading;

  // Notify parent if access is blocked
  React.useEffect(() => {
    if (!isChecking && !effectiveAccessGranted && onAccessBlocked) {
      onAccessBlocked();
    }
  }, [isChecking, effectiveAccessGranted, onAccessBlocked]);

  // Loading state
  if (isChecking && showLoading) {
    if (compact) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] p-8">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
        <p className={cn(
          "text-sm",
          isDark ? "text-gray-400" : "text-gray-500"
        )}>
          Checking access...
        </p>
      </div>
    );
  }

  // Access granted - render children with proper height inheritance
  if (effectiveAccessGranted) {
    return <div className="h-full w-full">{children}</div>;
  }

  // Access blocked - show message
  const message = customBlockedMessage || (
    requireMailbox
      ? (accessBlockedReason || `You need an active work email to use ${featureName}. Please contact your administrator.`)
      : `You do not have access to ${featureName}. Please contact your administrator.`
  );

  if (compact) {
    return (
      <div className={cn(
        "flex items-center gap-3 p-4 rounded-lg border",
        isDark
          ? "bg-amber-900/20 border-amber-700/30 text-amber-200"
          : "bg-amber-50 border-amber-200 text-amber-800"
      )}>
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm">{message}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      {/* Icon */}
      <div
        className={cn(
          "w-20 h-20 rounded-full flex items-center justify-center mb-6",
          isDark ? "bg-gray-800" : "bg-gray-100"
        )}
      >
        {requireMailbox ? (
          <Mail
            className={cn(
              "w-10 h-10",
              isDark ? "text-gray-400" : "text-gray-500"
            )}
          />
        ) : (
          <Lock
            className={cn(
              "w-10 h-10",
              isDark ? "text-gray-400" : "text-gray-500"
            )}
          />
        )}
      </div>

      {/* Title */}
      <h2 className={cn(
        "text-xl font-semibold mb-2",
        isDark ? "text-white" : "text-gray-900"
      )}>
        {requireMailbox ? 'Work Email Required' : 'Access Required'}
      </h2>

      {/* Message */}
      <p className={cn(
        "text-sm max-w-md mb-6",
        isDark ? "text-gray-400" : "text-gray-600"
      )}>
        {requireMailbox
          ? 'You need a work email account to use Total Connect. Please contact your administrator to set up your mailbox.'
          : message}
      </p>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        {requireMailbox && <RequestMailboxButton primaryColor={primaryColor} isDark={isDark} />}
      </div>

      {/* Help text */}
      <p className={cn(
        "text-xs mt-6",
        isDark ? "text-gray-500" : "text-gray-400"
      )}>
        Total Connect features include: Email, Calendar, Messenger, Calls, Cloud Storage, and Teams
      </p>
    </div>
  );
}

/**
 * Request Mailbox Button with Modal
 */
function RequestMailboxButton({ primaryColor, isDark }: { primaryColor: string; isDark: boolean }) {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { user, client } = useAuth();

  const handleSubmit = async () => {
    if (!message.trim()) {
      notify.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await fetch('/api/v1/request-mailbox', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          userName: user?.first_name && user?.last_name 
            ? `${user.first_name} ${user.last_name}` 
            : user?.email || 'Unknown User',
          userEmail: user?.email || '',
        }),
      });

      const data = await response.json();
      if (data.success) {
        notify.success('Request sent successfully! Your administrator will be notified.');
        setShowModal(false);
        setMessage('');
      } else {
        notify.error(data.error || 'Failed to send request');
      }
    } catch (error) {
      console.error('Error sending mailbox request:', error);
      notify.error('Failed to send request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
        style={{ backgroundColor: primaryColor }}
      >
        <UserPlus className="w-4 h-4" />
        Request Mailbox
      </button>

      {/* Request Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className={cn(
            "w-full max-w-md rounded-xl shadow-xl",
            isDark ? "bg-gray-800" : "bg-white"
          )}>
            {/* Header */}
            <div className={cn(
              "flex items-center justify-between px-6 py-4 border-b",
              isDark ? "border-gray-700" : "border-gray-200"
            )}>
              <h3 className={cn(
                "text-lg font-semibold",
                isDark ? "text-white" : "text-gray-900"
              )}>
                Request Work Mailbox
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className={cn(
                  "p-1 rounded-lg transition-colors",
                  isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
                )}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4">
              <p className={cn(
                "text-sm mb-4",
                isDark ? "text-gray-400" : "text-gray-600"
              )}>
                Send a request to your account administrators to set up your work email mailbox.
              </p>

              <label className={cn(
                "block text-sm font-medium mb-2",
                isDark ? "text-gray-300" : "text-gray-700"
              )}>
                Message to Administrator
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hi, I would like to request a work email mailbox to access Total Connect features..."
                rows={4}
                className={cn(
                  "w-full px-3 py-2 rounded-lg border text-sm resize-none",
                  "focus:outline-none focus:ring-2",
                  isDark 
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:ring-blue-500/50" 
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500/50"
                )}
              />

              <p className={cn(
                "text-xs mt-2",
                isDark ? "text-gray-500" : "text-gray-400"
              )}>
                Your name ({user?.first_name} {user?.last_name}) and email ({user?.email}) will be included in the request.
              </p>
            </div>

            {/* Footer */}
            <div className={cn(
              "flex justify-end gap-3 px-6 py-4 border-t",
              isDark ? "border-gray-700" : "border-gray-200"
            )}>
              <button
                onClick={() => setShowModal(false)}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                  isDark 
                    ? "bg-gray-700 hover:bg-gray-600 text-white" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={sending || !message.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Hook variant for components that need to check access programmatically
 */
export function useTotalConnectAccess() {
  const { hasActiveMailbox, isLoading, accessBlockedReason, workEmail } = useWorkEmail();
  const { hasAnyMailbox: hasAnyMailboxCrossClient, loading: accessibleEmailLoading } = useAccessibleEmailClients();

  const hasAccess = hasActiveMailbox || hasAnyMailboxCrossClient;
  const isChecking = isLoading || accessibleEmailLoading;

  return {
    hasAccess,
    isChecking,
    blockedReason: accessBlockedReason,
    workEmail: workEmail?.email_address || null,
    /** True when the user has a mailbox at a client other than the active one. */
    hasCrossClientMailbox: !hasActiveMailbox && hasAnyMailboxCrossClient,
  };
}

export default TotalConnectAccessGate;